import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ValidatedTextField from '../../inputs/ValidatedTextField';
import ValidatedSelectField from '../../inputs/ValidatedSelectField';
import states from '../../../constants/stateSelectOptions';
import { postCheckoutInitial } from '../../../containers/customerDetails/customerDetailsSlice';
import { useMembership } from '../../../contexts/MembershipServiceContext';
import { getBrowserSignature } from '../../../utils/getBrowserSignature';

import Stack from '@mui/material/Stack';

const BillingInfo = ({
	formData,
	errors,
	handleInputChange,
	handleInputBlur
}) => {
	const { t } = useTranslation('billingInfo');
	const dispatch = useDispatch();
	const { result: campaignResult } = useMembership();
	const signatureObj = getBrowserSignature();
	const lastCapturedEmailRef = useRef('');
	const { isLoading } = useSelector(
		(state) => state.customerDetailsSlice?.postCheckoutInitial || {}
	);

	const handleEmailBlur = (name, value) => {
		handleInputBlur(name, value);

		const email = value?.trim().toLowerCase() || '';

		if (
			email &&
			email.includes('@') &&
			campaignResult?.sku &&
			campaignResult?.source &&
			email !== lastCapturedEmailRef.current &&
			!isLoading
		) {
			lastCapturedEmailRef.current = email;

			dispatch(
				postCheckoutInitial({
					uuid: uuidv4(),
					email,
					sku: campaignResult.sku,
					source: campaignResult.source,
					telemetry: {
						signature: signatureObj.signatureKey,
						color_depth: signatureObj.colorDepth,
						screen_height: signatureObj.height,
						screen_width: signatureObj.width,
						user_agent: signatureObj.userAgent,
						is_javascript_enabled: signatureObj.isJavascriptEnabled,
						is_java_enabled: signatureObj.isJavaEnabled,
						timezone: signatureObj.timezone
					}
				})
			);
		}
	};

	return (
		<Stack spacing={2}>
			<ValidatedTextField
				name='email'
				label={t('email')}
				type='email'
				formData={formData}
				handleInputChange={handleInputChange}
				handleInputBlur={handleEmailBlur}
				required
				errors={errors}
			/>

			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
				<ValidatedTextField
					name='firstName'
					label={t('firstName')}
					formData={formData}
					handleInputChange={handleInputChange}
					handleInputBlur={handleInputBlur}
					required
					errors={errors}
				/>
				<ValidatedTextField
					name='lastName'
					label={t('lastName')}
					formData={formData}
					handleInputChange={handleInputChange}
					handleInputBlur={handleInputBlur}
					required
					errors={errors}
				/>
			</Stack>

			<ValidatedTextField
				name='address1'
				label={t('address')}
				formData={formData}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
				required
				errors={errors}
			/>

			<ValidatedTextField
				name='address2'
				label={t('address2')}
				formData={formData}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
				errors={{}}
			/>

			<ValidatedTextField
				name='city'
				label={t('city')}
				formData={formData}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
				required
				errors={errors}
			/>

			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
				<ValidatedSelectField
					name='state'
					label={t('state')}
					formData={formData}
					handleInputChange={handleInputChange}
					handleInputBlur={handleInputBlur}
					options={states}
					required
					errors={errors}
				/>
				<ValidatedTextField
					name='zipCode'
					label={t('zip')}
					formData={formData}
					handleInputChange={handleInputChange}
					handleInputBlur={handleInputBlur}
					required
					errors={errors}
					inputMode='numeric'
				/>
			</Stack>

			<ValidatedTextField
				name='phone'
				label={t('phone')}
				type='tel'
				formData={formData}
				handleInputChange={handleInputChange}
				handleInputBlur={handleInputBlur}
				required
				errors={errors}
			/>
		</Stack>
	);
};

BillingInfo.propTypes = {
	formData: PropTypes.object,
	errors: PropTypes.object,
	handleInputChange: PropTypes.func,
	handleInputBlur: PropTypes.func
};

export default BillingInfo;
